import { isEmpty } from 'lodash';

import { JobStatus, JobType } from 'interfaces/job';
import { PipRunNameEnum } from 'interfaces/jobs/pipsRunNames';
import useCellRulePanels from 'utils/queryHooks/cellRule/useCellRulePanels';
import { useJobs } from 'utils/queryHooks/jobs/useJobs';

export const shouldRevealOptionalSelector = ({
  isLoading,
  hasOptions,
  hasSelection,
}: {
  isLoading: boolean;
  hasOptions: boolean;
  hasSelection: boolean;
}) => !isLoading && (hasOptions || hasSelection);

const useOptionalJobAvailability = ({
  studyId,
  jobType,
  enabled,
}: {
  studyId?: string;
  jobType: JobType;
  enabled: boolean;
}) => {
  const canQuery = Boolean(enabled && studyId);
  const { data, isLoading, isFetched } = useJobs({
    studyId,
    page: 1,
    pageSize: 1,
    additionalFilters: {
      types: [jobType],
      statuses: [JobStatus.Completed],
    },
    enabled: canQuery,
    noInitialData: true,
    keepPreviousData: false,
  });

  return {
    isLoading: canQuery && (isLoading || !isFetched),
    hasJobs: Boolean(data?.totalJobs) || !isEmpty(data?.jobs),
  };
};

export const useGeneralInputAvailability = ({
  studyId,
  pipRunName,
  enabled,
}: {
  studyId?: string;
  pipRunName?: string;
  enabled: boolean;
}) => {
  const shouldQueryCellTypingInputs =
    enabled &&
    Boolean(studyId) &&
    (pipRunName === PipRunNameEnum.CellTyping || pipRunName === PipRunNameEnum.GenericPipsRun);
  const shouldQueryNeighborhoodInputs =
    enabled &&
    Boolean(studyId) &&
    (pipRunName === PipRunNameEnum.NeighborhoodClusters || pipRunName === PipRunNameEnum.GenericPipsRun);

  const { isLoading: isLoadingThresholdJobs, hasJobs: hasThresholdJobs } = useOptionalJobAvailability({
    studyId,
    jobType: JobType.MultiplexThreshold,
    enabled: shouldQueryCellTypingInputs,
  });
  const {
    data: panels,
    isLoading: isLoadingPanelQuery,
    isFetched: isPanelsFetched,
  } = useCellRulePanels(studyId, {
    enabled: shouldQueryCellTypingInputs,
  });
  const { isLoading: isLoadingCellTypingJobs, hasJobs: hasCellTypingJobs } = useOptionalJobAvailability({
    studyId,
    jobType: JobType.MultiplexCellTyping,
    enabled: shouldQueryCellTypingInputs,
  });
  const { isLoading: isLoadingNeighborhoodJobs, hasJobs: hasNeighborhoodJobs } = useOptionalJobAvailability({
    studyId,
    jobType: JobType.MultiplexNeighborhoodClusters,
    enabled: shouldQueryNeighborhoodInputs,
  });

  const isLoadingPanels = shouldQueryCellTypingInputs && (isLoadingPanelQuery || !isPanelsFetched);
  const isLoadingGeneralInputs =
    isLoadingThresholdJobs || isLoadingPanels || isLoadingCellTypingJobs || isLoadingNeighborhoodJobs;

  return {
    isLoadingThresholdJobs,
    hasThresholdJobs,
    isLoadingPanels,
    hasPanels: !isEmpty(panels),
    isLoadingCellTypingJobs,
    hasCellTypingJobs,
    isLoadingNeighborhoodJobs,
    hasNeighborhoodJobs,
    isLoadingGeneralInputs,
  };
};
